import React from "react";
import CopilotIcon from "components/shared/CopilotIcon/CopilotIcon";
import { Box, Tooltip, Typography } from "@mui/material";

const statusChipStyles = {
  Joined: { bg: "#eaf6ee", color: "#1f7a45", border: "#b9dfc8" },
  Requested: { bg: "#fff8e8", color: "#9b6b00", border: "#ffe2aa" },
  Pending: { bg: "#fff8e8", color: "#9b6b00", border: "#ffe2aa" },
  Rejected: { bg: "#ffefef", color: "#c43838", border: "#f3caca" },
  Removed: { bg: "#ffefef", color: "#c43838", border: "#f3caca" },
  "Remove-Requested": { bg: "#ffefef", color: "#c43838", border: "#f3caca" },
  "Not-Requested": { bg: "#f1f5f9", color: "#475569", border: "#d0d7de" },
};

const InfoRow = ({ label, value }) => {
  if (!value) return null;
  return (
    <Box sx={{ mb: 1.25 }}>
      <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", letterSpacing: 0.8, textTransform: "uppercase", mb: 0.25 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 13, color: "#1e293b", fontWeight: 500, wordBreak: "break-word", lineHeight: 1.4 }}>
        {value}
      </Typography>
    </Box>
  );
};


// The orgs this user has Joined on a platform, most recently updated first.
export const getJoinedOrganizations = (user, platformId) => {
  if (!user || !platformId) return [];
  const organizations = user?.platforms?.[platformId]?.organizations || {};
  return Object.entries(organizations)
    .filter(([, value]) => value?.membership_status === "Joined")
    .map(([id, value]) => ({ id, ...value }))
    .sort((a, b) => {
      const aDate = a?.status_history?.[a.status_history.length - 1]?.changed_date;
      const bDate = b?.status_history?.[b.status_history.length - 1]?.changed_date;
      return new Date(bDate || 0) - new Date(aDate || 0);
    });
};

// Orgs the user holds a Copilot license in — at the top level of the user, or
// inside their copilot_budget block, depending on the endpoint.
export const getLicensedOrgIds = (user) =>
  new Set(user?.licensed_organizations ?? user?.copilot_budget?.licensed_organizations ?? []);

// "Cost & billing profile" card: GitHub cost center, business name/area/line and
// the RC Leader / GPS badges. Shared by the User Profile and merged user modals.
// `compact` is the denser layout for the merged modal: the fields flow across
// the width instead of stacking, labels and values are smaller, and the RC Leader
// / GPS badges sit beside the title.
const CompactField = ({ label, value }) => {
  if (!value) return null;
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: 9, fontWeight: 700, color: "#94a3b8", letterSpacing: 0.6, textTransform: "uppercase", lineHeight: 1.3 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 12, color: "#1e293b", fontWeight: 500, wordBreak: "break-word", lineHeight: 1.35 }}>{value}</Typography>
    </Box>
  );
};

const badgeSx = (bg, color, border) => ({ background: bg, color, border: `1px solid ${border}`, borderRadius: 3, px: 1, py: 0.2, fontSize: 10.5, fontWeight: 500, whiteSpace: "nowrap" });

export const UserCostBillingCard = ({ user, platformId, costCenterById = {}, compact = false }) => {
  const costCenterId = user?.platforms?.[platformId]?.cost_center_id;
  const costCenterName = costCenterById[costCenterId] || costCenterId || "";
  const opData = user?.op_data || {};
  const hasOpProfile = Boolean(
    costCenterName ||
    opData.rc_leader_name ||
    opData.gps_indicator ||
    opData.crcc_business_name ||
    opData.crcc_business_area ||
    opData.crcc_business_line
  );
  if (compact) {
    if (!hasOpProfile) return null;
    const gps = opData.gps_indicator;
    return (
      <Box sx={{ background: "#fcfdfd", border: "1px solid #edf1f4", borderRadius: 2, px: 1.75, py: 1.25 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, flexWrap: "wrap", mb: 1 }}>
          <Typography sx={{ fontWeight: 700, color: "#0b756c", fontSize: 11, letterSpacing: 0.5 }}>COST &amp; BILLING PROFILE</Typography>
          <Box sx={{ display: "flex", gap: 0.6, flexWrap: "wrap" }}>
            {opData.rc_leader_name && <Box sx={badgeSx("#eef6ff", "#0969da", "#c6ddff")}>RC Leader: {opData.rc_leader_name}</Box>}
            {gps && <Box sx={badgeSx("#ecfdf3", "#116329", "#b4e2c8")}>{String(gps).toUpperCase() === "GPS" ? "GPS" : `GPS: ${gps}`}</Box>}
          </Box>
        </Box>
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: "8px 20px" }}>
          <CompactField label="GitHub Cost Center" value={costCenterName} />
          <CompactField label="Business Name" value={opData.crcc_business_name} />
          <CompactField label="Business Area" value={opData.crcc_business_area} />
          <CompactField label="Business Line" value={opData.crcc_business_line} />
        </Box>
      </Box>
    );
  }

  return (
    <>
              {/* Cost & Billing Section */}
              {hasOpProfile && (
                <Box
                  sx={{
                    background: "#fcfdfd",
                    border: "1px solid #edf1f4",
                    borderRadius: 2.5,
                    p: 2,
                    mb: 1.5,
                  }}
                >
                  <Typography sx={{ fontWeight: 700, color: "#0b756c", fontSize: 12, mb: 1.5, letterSpacing: 0.5 }}>
                    COST &amp; BILLING PROFILE
                  </Typography>

                  <InfoRow label="GitHub Cost Center" value={costCenterName} />
                  <InfoRow label="Business Name" value={opData.crcc_business_name} />

                  <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
                    <InfoRow label="Business Area" value={opData.crcc_business_area} />
                    <InfoRow label="Business Line" value={opData.crcc_business_line} />
                  </Box>

                  {/* RC Leader & GPS badges */}
                  {(opData.rc_leader_name || opData.gps_indicator) && (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1 }}>
                      {opData.rc_leader_name && (
                        <Box
                          sx={{
                            background: "#eef6ff",
                            color: "#0969da",
                            border: "1px solid #c6ddff",
                            borderRadius: 3,
                            px: 1.25,
                            py: 0.4,
                            fontSize: 11,
                            fontWeight: 500,
                          }}
                        >
                          RC Leader: {opData.rc_leader_name}
                        </Box>
                      )}
                      {opData.gps_indicator && (
                        <Box
                          sx={{
                            background: "#ecfdf3",
                            color: "#116329",
                            border: "1px solid #b4e2c8",
                            borderRadius: 3,
                            px: 1.25,
                            py: 0.4,
                            fontSize: 11,
                            fontWeight: 600,
                          }}
                        >
                          GPS: {opData.gps_indicator}
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>
              )}

              {/* If no op_data but has cost center, still show it */}
              {!hasOpProfile && costCenterName && (
                <Box sx={{ mb: 1.5 }}>
                  <InfoRow label="GitHub Cost Center" value={costCenterName} />
                </Box>
              )}
    </>
  );
};

// Joined organizations: name, whether the user holds a Copilot license there,
// membership status and last update.
export const UserOrganizationsTable = ({ orgs, orgIdToName = {}, licensedOrgIds = new Set(), maxHeight = 420 }) => (
                <Box
                  sx={{
                    flex: 1,
                    border: "1px solid #edf1f4",
                    borderRadius: 2,
                    overflow: "hidden",
                    maxHeight,
                    overflowY: "auto",
                    background: "#fff",
                  }}
                >
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={{ background: "#f8fafc", position: "sticky", top: 0, zIndex: 1 }}>
                        <th style={{ textAlign: "left", padding: "10px 14px", fontSize: 12, fontWeight: 700, color: "#0b756c", borderBottom: "2px solid #e0e7ef" }}>
                          Organization
                        </th>
                        <th style={{ textAlign: "center", padding: "10px 14px", fontSize: 12, fontWeight: 700, color: "#0b756c", borderBottom: "2px solid #e0e7ef" }}>
                          Copilot
                        </th>
                        <th style={{ textAlign: "center", padding: "10px 14px", fontSize: 12, fontWeight: 700, color: "#0b756c", borderBottom: "2px solid #e0e7ef" }}>
                          Status
                        </th>
                        <th style={{ textAlign: "center", padding: "10px 14px", fontSize: 12, fontWeight: 700, color: "#0b756c", borderBottom: "2px solid #e0e7ef" }}>
                          Last Updated
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {orgs.map((org) => {
                        const chipStyle = statusChipStyles[org.membership_status] || statusChipStyles["Not-Requested"];
                        const lastHistory = org?.status_history?.[org.status_history.length - 1];
                        return (
                          <tr
                            key={org.id}
                            style={{
                              borderBottom: "1px solid #f0f0f0",
                              borderLeft: `3px solid ${chipStyle.border}`,
                            }}
                          >
                            <td style={{ padding: "10px 14px", fontSize: 13, fontWeight: 500, color: "#1f2328" }}>
                              {orgIdToName[org.id] || org.id}
                            </td>
                            <td style={{ padding: "10px 14px", textAlign: "center" }}>
                              {licensedOrgIds.has(org.id) && (
                                <Tooltip title="Has a Copilot license in this organization" arrow>
                                  <CopilotIcon aria-label="Copilot licensed" sx={{ fontSize: 18, color: "#1a1a1a", verticalAlign: "middle" }} />
                                </Tooltip>
                              )}
                            </td>
                            <td style={{ padding: "10px 14px", textAlign: "center" }}>
                              <span
                                style={{
                                  background: chipStyle.bg,
                                  color: chipStyle.color,
                                  padding: "3px 12px",
                                  borderRadius: 999,
                                  fontSize: 11,
                                  fontWeight: 600,
                                  letterSpacing: 0.2,
                                }}
                              >
                                {org.membership_status || "Unknown"}
                              </span>
                            </td>
                            <td style={{ padding: "10px 14px", textAlign: "center", fontSize: 12, color: "#667085" }}>
                              {lastHistory?.changed_date
                                ? new Date(lastHistory.changed_date).toLocaleDateString()
                                : "-"}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </Box>
);
